import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    alpha,
    Chip,
    Grid,
    Card,
    CardContent,
    Tooltip,
    Snackbar,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditSaleDialog from '../../../components/stocks/EditSaleDialog';
import { deleteSale } from '../../../utils/stockSales';
import { useSupabase } from '../../../contexts/SupabaseContext';
import { formatCurrency, formatDate } from '../../../utils/format';
import GuidedTour from '../../../components/tour/GuidedTour';
import TourButton from '../../../components/tour/TourButton';
import useTour from '../../../components/tour/useTour';
import { TOURS } from '../../../components/tour/tours';

// Per-share prices need paise; totals elsewhere are shown in whole rupees
const formatPrice = (amount) => new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
}).format(amount);

const DAY_MS = 24 * 60 * 60 * 1000;
const parseDay = (value) => (value ? new Date(`${String(value).slice(0, 10)}T00:00:00`) : null);

// What the sold shares cost: average buy price × units (net received − P&L for older rows)
const costBasisOf = (sale) => {
    const avg = parseFloat(sale.average_buy_price);
    const units = parseFloat(sale.units_sold);
    if (avg > 0 && units > 0) return avg * units;
    return parseFloat(sale.net_amount || 0) - parseFloat(sale.profit_loss || 0);
};

// Days between the average buy date and the sale date (null if the buy date wasn't recorded)
const holdingDaysOf = (sale) => {
    const bought = parseDay(sale.avg_buy_date);
    const sold = parseDay(sale.sale_date);
    if (!bought || !sold) return null;
    return Math.round((sold - bought) / DAY_MS);
};

// Return per year, as a fraction. Held a year or more: compounded (CAGR).
// Held less than a year: simple yearly rate, since compounding a few weeks'
// return up to a year exaggerates it.
const annualise = (returnFraction, days) => {
    if (returnFraction === null || !days || days <= 0) return null;
    if (days >= 365) return Math.pow(1 + returnFraction, 365 / days) - 1;
    return returnFraction * (365 / days);
};

// Money-weighted return per year across sales (XIRR): each sale is −cost on its
// buy date and +net received on its sale date. Solved by bisection.
const xirr = (flows) => {
    if (flows.length < 2 || !flows.some(f => f.amount < 0) || !flows.some(f => f.amount > 0)) return null;
    const start = Math.min(...flows.map(f => f.time));
    const npv = (rate) => flows.reduce((sum, f) => sum + f.amount / Math.pow(1 + rate, (f.time - start) / (365 * DAY_MS)), 0);
    let low = -0.9999;
    let high = 100;
    if (npv(low) * npv(high) > 0) return null;
    for (let i = 0; i < 200; i++) {
        const mid = (low + high) / 2;
        if (npv(low) * npv(mid) <= 0) high = mid; else low = mid;
    }
    return (low + high) / 2;
};

const formatHolding = (days) => {
    if (days === null) return null;
    if (days < 31) return `${days} day${days === 1 ? '' : 's'}`;
    const months = Math.round(days / 30.44);
    if (months < 12) return `${months} mo`;
    const years = Math.floor(months / 12);
    const rest = months % 12;
    return `${years} yr${years > 1 ? 's' : ''}${rest ? ` ${rest} mo` : ''}`;
};

const formatPct = (fraction) => {
    if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return '—';
    const pct = fraction * 100;
    return `${pct < 0 ? '−' : pct > 0 ? '+' : ''}${Math.abs(pct).toFixed(2)}%`;
};

const pctColor = (fraction) => (fraction === null || fraction === undefined || !Number.isFinite(fraction)
    ? 'text.secondary'
    : fraction >= 0 ? 'success.main' : 'error.main');

const StockPLReport = () => {
    const navigate = useNavigate();
    const { supabase, user } = useSupabase();
    const [loading, setLoading] = useState(true);
    const [sales, setSales] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'sale_date', direction: 'desc' });
    const [editingSaleId, setEditingSaleId] = useState(null);
    const [message, setMessage] = useState(null);
    const [saleToDelete, setSaleToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const tour = useTour('inv-equity-stocks-pl-report', { ready: !loading });

    // Fetch stock sales from Supabase
    const fetchSales = async ({ quiet = false } = {}) => {
        if (!user) {
            console.log('No user found');
            setLoading(false);
            return;
        }

        try {
            if (!quiet) setLoading(true);

            const { data, error } = await supabase
                .from('stock_sales')
                .select('*')
                .eq('user_id', user.id)
                .order('sale_date', { ascending: false });

            if (error) throw error;

            setSales(data || []);
        } catch (error) {
            console.error('Error fetching sales:', error);
            setSales([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSales();
    }, [user]);

    // Per-sale return figures
    const salesWithReturns = useMemo(() => sales.map((sale) => {
        const cost = costBasisOf(sale);
        const returnFraction = cost > 0 ? parseFloat(sale.profit_loss || 0) / cost : null;
        const holdingDays = holdingDaysOf(sale);
        return {
            ...sale,
            cost,
            returnFraction,
            holdingDays,
            annualFraction: annualise(returnFraction, holdingDays),
        };
    }), [sales]);

    // Calculate totals
    const totals = useMemo(() => {
        const totalSales = sales.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0);
        const totalCharges = sales.reduce((sum, sale) => sum + parseFloat(sale.charges || 0), 0);
        const totalNetAmount = sales.reduce((sum, sale) => sum + parseFloat(sale.net_amount || 0), 0);
        const totalProfitLoss = sales.reduce((sum, sale) => sum + parseFloat(sale.profit_loss || 0), 0);
        const totalCostBasis = salesWithReturns.reduce((sum, sale) => sum + sale.cost, 0);
        const totalReturn = totalCostBasis > 0 ? totalProfitLoss / totalCostBasis : null;

        // Return per year needs buy dates; sales recorded before they were stored are left out
        const dated = salesWithReturns.filter(sale => sale.holdingDays !== null && sale.holdingDays > 0 && sale.cost > 0);
        const datedCost = dated.reduce((sum, sale) => sum + sale.cost, 0);
        const avgHoldingDays = datedCost > 0
            ? dated.reduce((sum, sale) => sum + sale.cost * sale.holdingDays, 0) / datedCost
            : null;
        let annualReturn = null;
        if (avgHoldingDays !== null) {
            if (avgHoldingDays >= 365) {
                annualReturn = xirr(dated.flatMap(sale => [
                    { amount: -sale.cost, time: parseDay(sale.avg_buy_date).getTime() },
                    { amount: parseFloat(sale.net_amount || 0), time: parseDay(sale.sale_date).getTime() },
                ]));
            } else {
                const datedPL = dated.reduce((sum, sale) => sum + parseFloat(sale.profit_loss || 0), 0);
                annualReturn = annualise(datedPL / datedCost, avgHoldingDays);
            }
        }

        return {
            totalSales,
            totalCharges,
            totalNetAmount,
            totalProfitLoss,
            totalCostBasis,
            totalReturn,
            annualReturn,
            avgHoldingDays,
            undatedCount: salesWithReturns.length - dated.length,
            profitCount: sales.filter(s => parseFloat(s.profit_loss) >= 0).length,
            lossCount: sales.filter(s => parseFloat(s.profit_loss) < 0).length,
        };
    }, [sales, salesWithReturns]);

    // Sort sales
    const sortedSales = useMemo(() => {
        return [...salesWithReturns].sort((a, b) => {
            let aValue, bValue;

            switch (sortConfig.key) {
                case 'stock_name':
                    aValue = a.stock_name;
                    bValue = b.stock_name;
                    break;
                case 'sale_date':
                    aValue = new Date(a.sale_date);
                    bValue = new Date(b.sale_date);
                    break;
                case 'units_sold':
                    aValue = parseFloat(a.units_sold);
                    bValue = parseFloat(b.units_sold);
                    break;
                case 'sale_price':
                    aValue = parseFloat(a.sale_price);
                    bValue = parseFloat(b.sale_price);
                    break;
                case 'total_amount':
                    aValue = parseFloat(a.total_amount);
                    bValue = parseFloat(b.total_amount);
                    break;
                case 'profit_loss':
                    aValue = parseFloat(a.profit_loss);
                    bValue = parseFloat(b.profit_loss);
                    break;
                case 'return':
                    aValue = a.returnFraction ?? -Infinity;
                    bValue = b.returnFraction ?? -Infinity;
                    break;
                case 'annual':
                    aValue = a.annualFraction ?? -Infinity;
                    bValue = b.annualFraction ?? -Infinity;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [salesWithReturns, sortConfig]);

    const handleSaleEdited = async ({ unitsChanged, unitsDifference }) => {
        setEditingSaleId(null);
        await fetchSales({ quiet: true });
        const n = Math.abs(unitsDifference);
        setMessage(unitsChanged
            ? `Sale updated. ${n} unit${n === 1 ? '' : 's'} ${unitsDifference > 0 ? 'removed from' : 'returned to'} your holdings.`
            : 'Sale updated.');
    };

    const closeDeleteDialog = () => {
        if (deleting) return;
        setSaleToDelete(null);
        setDeleteError(null);
    };

    const handleDeleteSale = async () => {
        if (!saleToDelete || deleting) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            const { unitsRestored } = await deleteSale({ supabase, userId: user.id, saleId: saleToDelete.id });
            await fetchSales({ quiet: true });
            setMessage(`Sale deleted. ${unitsRestored} unit${unitsRestored === 1 ? '' : 's'} of ${saleToDelete.stock_name} returned to your holdings.`);
            setSaleToDelete(null);
        } catch (error) {
            setDeleteError(`Failed to delete sale: ${error.message || 'Unknown error'}`);
        } finally {
            setDeleting(false);
        }
    };

    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{
            p: { xs: 2, md: 4 },
            minHeight: '100vh'
        }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <IconButton
                    onClick={() => navigate('/investments/equity/stocks')}
                    aria-label="Back to Stocks"
                    sx={{
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        '&:hover': { bgcolor: 'action.hover' }
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography
                    variant="h4"
                    sx={{
                        ml: 2,
                        fontWeight: 700,
                        color: 'text.primary',
                    }}
                >
                    Stock P&L Report
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                    <TourButton onClick={tour.start} />
                </Box>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }} data-tour="inv-plr-summary">
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <Card sx={{
                        borderRadius: '16px',
                        background: theme => alpha(theme.palette.background.paper, 0.6),
                        backdropFilter: 'blur(10px)',
                        boxShadow: 'var(--fx-card-shadow)'
                    }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="body2">
                                Total Sales Amount
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {formatCurrency(totals.totalSales)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {sales.length} transactions
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <Card sx={{
                        borderRadius: '16px',
                        background: theme => alpha(theme.palette.background.paper, 0.6),
                        backdropFilter: 'blur(10px)',
                        boxShadow: 'var(--fx-card-shadow)'
                    }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="body2">
                                Total Charges
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                {formatCurrency(totals.totalCharges)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Fees & taxes
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <Card sx={{
                        borderRadius: '16px',
                        background: theme => alpha(theme.palette.background.paper, 0.6),
                        backdropFilter: 'blur(10px)',
                        boxShadow: 'var(--fx-card-shadow)'
                    }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="body2">
                                Net Amount Received
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: 'info.main' }}>
                                {formatCurrency(totals.totalNetAmount)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                After charges
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <Card sx={{
                        borderRadius: '16px',
                        background: theme => alpha(theme.palette.background.paper, 0.6),
                        backdropFilter: 'blur(10px)',
                        boxShadow: 'var(--fx-card-shadow)'
                    }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="body2">
                                Total P&L
                            </Typography>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 600,
                                    color: totals.totalProfitLoss >= 0 ? 'success.main' : 'error.main'
                                }}
                            >
                                {totals.totalProfitLoss < 0 ? '−' : ''}{formatCurrency(Math.abs(totals.totalProfitLoss))}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                <Chip
                                    icon={<TrendingUpIcon />}
                                    label={`${totals.profitCount} profit`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<TrendingDownIcon />}
                                    label={`${totals.lossCount} loss`}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <Card data-tour="inv-plr-return" sx={{
                        borderRadius: '16px',
                        background: theme => alpha(theme.palette.background.paper, 0.6),
                        backdropFilter: 'blur(10px)',
                        boxShadow: 'var(--fx-card-shadow)'
                    }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="body2">
                                Return
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: pctColor(totals.totalReturn) }}>
                                {formatPct(totals.totalReturn)}
                            </Typography>
                            <Tooltip
                                arrow
                                title={totals.avgHoldingDays !== null && totals.avgHoldingDays >= 365
                                    ? 'Return per year across your sales, taking into account when you bought and sold each (XIRR).'
                                    : 'Your average holding is under a year, so this is the simple yearly rate (not compounded).'}
                            >
                                <Typography variant="caption" sx={{ color: pctColor(totals.annualReturn), display: 'block', cursor: 'help' }}>
                                    {formatPct(totals.annualReturn)} per year
                                </Typography>
                            </Tooltip>
                            {totals.undatedCount > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Per year leaves out {totals.undatedCount} older sale{totals.undatedCount > 1 ? 's' : ''} without a buy date
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Sales Table */}
            <Paper data-tour="inv-plr-table" sx={{
                borderRadius: '16px',
                background: theme => alpha(theme.palette.background.paper, 0.4),
                backdropFilter: 'blur(10px)',
                overflow: 'hidden'
            }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                                    onClick={() => handleSort('sale_date')}
                                >
                                    Sale Date
                                </TableCell>
                                <TableCell
                                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                                    onClick={() => handleSort('stock_name')}
                                >
                                    Stock Name
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                                    onClick={() => handleSort('units_sold')}
                                >
                                    Units Sold
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                                    onClick={() => handleSort('sale_price')}
                                >
                                    Sale Price
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                                    onClick={() => handleSort('total_amount')}
                                >
                                    Total Amount
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    Charges
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    Net Amount
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                                    onClick={() => handleSort('profit_loss')}
                                >
                                    P&L
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                                    onClick={() => handleSort('return')}
                                >
                                    <Tooltip arrow title="Profit or loss as a % of what the sold shares cost (after charges)">
                                        <span>Return</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                                    onClick={() => handleSort('annual')}
                                    data-tour="inv-plr-peryear"
                                >
                                    <Tooltip arrow title="Return per year. Held a year or more: compounded (CAGR). Less than a year: simple yearly rate. Shows — for sales recorded before buy dates were saved.">
                                        <span>Per Year</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedSales.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            No stock sales found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedSales.map((sale, index) => (
                                    <TableRow
                                        key={sale.id}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: theme => alpha(theme.palette.action.hover, 0.1)
                                            }
                                        }}
                                    >
                                        <TableCell>{formatDate(sale.sale_date)}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {sale.stock_name}
                                            </Typography>
                                            {sale.stock_symbol && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {sale.stock_symbol}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {parseFloat(sale.units_sold).toFixed(2)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatPrice(sale.sale_price)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatCurrency(sale.total_amount)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="warning.main">
                                                {formatCurrency(sale.charges)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="medium">
                                                {formatCurrency(sale.net_amount)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip
                                                icon={parseFloat(sale.profit_loss) >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                                label={`${parseFloat(sale.profit_loss) < 0 ? '−' : ''}${formatCurrency(Math.abs(sale.profit_loss))}`}
                                                size="small"
                                                color={parseFloat(sale.profit_loss) >= 0 ? 'success' : 'error'}
                                                sx={{ fontWeight: 600 }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: pctColor(sale.returnFraction) }}>
                                                {formatPct(sale.returnFraction)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: pctColor(sale.annualFraction) }}>
                                                {formatPct(sale.annualFraction)}
                                            </Typography>
                                            {sale.holdingDays !== null && (
                                                <Typography variant="caption" color="text.secondary">
                                                    held {formatHolding(sale.holdingDays)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }} data-tour={index === 0 ? 'inv-plr-actions' : undefined}>
                                            <Tooltip arrow title="Edit sale">
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Edit ${sale.stock_name} sale on ${formatDate(sale.sale_date)}`}
                                                    onClick={() => setEditingSaleId(sale.id)}
                                                    sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip arrow title="Delete sale">
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Delete ${sale.stock_name} sale on ${formatDate(sale.sale_date)}`}
                                                    onClick={() => setSaleToDelete(sale)}
                                                    sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <EditSaleDialog
                open={Boolean(editingSaleId)}
                saleId={editingSaleId}
                onClose={() => setEditingSaleId(null)}
                onSaved={handleSaleEdited}
            />
            <Dialog open={Boolean(saleToDelete)} onClose={closeDeleteDialog} aria-labelledby="delete-sale-title" maxWidth="xs" fullWidth>
                <DialogTitle id="delete-sale-title" sx={{ fontWeight: 600 }}>Delete this sale?</DialogTitle>
                <DialogContent>
                    {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
                    {saleToDelete && (
                        <>
                            <DialogContentText sx={{ mb: 2 }}>
                                The {parseFloat(saleToDelete.units_sold)} sold unit{parseFloat(saleToDelete.units_sold) === 1 ? '' : 's'} of <b>{saleToDelete.stock_name}</b> will go back into your holdings at their average buy price of {formatPrice(saleToDelete.average_buy_price)}.
                            </DialogContentText>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">Sold on</Typography>
                                <Typography variant="body2" sx={{ textAlign: 'right' }}>{formatDate(saleToDelete.sale_date)}</Typography>
                                <Typography variant="body2" color="text.secondary">Sale price</Typography>
                                <Typography variant="body2" sx={{ textAlign: 'right' }}>{formatPrice(saleToDelete.sale_price)}</Typography>
                                <Typography variant="body2" color="text.secondary">Net received</Typography>
                                <Typography variant="body2" sx={{ textAlign: 'right' }}>{formatCurrency(saleToDelete.net_amount)}</Typography>
                                <Typography variant="body2" color="text.secondary">P&L</Typography>
                                <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 600, color: parseFloat(saleToDelete.profit_loss) >= 0 ? 'success.main' : 'error.main' }}>
                                    {parseFloat(saleToDelete.profit_loss) < 0 ? '−' : '+'}{formatCurrency(Math.abs(saleToDelete.profit_loss))}
                                </Typography>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeDeleteDialog} disabled={deleting}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteSale}
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {deleting ? 'Deleting...' : 'Delete Sale'}
                    </Button>
                </DialogActions>
            </Dialog>

            <GuidedTour steps={TOURS['inv-equity-stocks-pl-report']} open={tour.open} onClose={tour.close} />

            <Snackbar
                open={Boolean(message)}
                autoHideDuration={4000}
                onClose={() => setMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" variant="filled" onClose={() => setMessage(null)}>
                    {message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default StockPLReport;